import * as React from 'react';
import { cn } from '@shared/utils/cn';

// Minimal, dependency-free Command primitive (shadcn-like API)
// Note: This is a lightweight emulation without cmdk; it focuses on structure and styling.

export function Command({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'rounded-md border bg-popover text-popover-foreground shadow-md',
        'flex h-auto w-full flex-col overflow-hidden',
        className,
      )}
      {...props}
    />
  );
}

export const CommandInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...props }, ref) => (
    <div className="flex items-center border-b px-2">
      <input
        ref={ref}
        className={cn(
          'flex h-9 w-full rounded-md bg-transparent py-2 text-sm outline-none',
          'placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...props}
      />
    </div>
  ),
);
CommandInput.displayName = 'CommandInput';

export function CommandList({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      role="listbox"
      className={cn('overflow-auto p-1', className)}
      {...props}
    />
  );
}

export function CommandEmpty({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('px-3 py-2 text-sm text-muted-foreground', className)} {...props} />
  );
}

export function CommandGroup({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('py-1', className)} {...props} />;
}

export const CommandItem = React.forwardRef<
  HTMLButtonElement,
  React.HTMLAttributes<HTMLButtonElement> & { selected?: boolean }
>(({ className, selected, ...props }, ref) => {
  return (
    <button
      ref={ref}
      role="option"
      aria-selected={selected}
      className={cn(
        'w-full select-none rounded-sm px-3 py-2 text-left text-sm',
        'hover:bg-accent focus:bg-accent',
        selected && 'bg-accent',
        className,
      )}
      {...props}
    />
  );
});
CommandItem.displayName = 'CommandItem';

export function CommandSeparator({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('my-1 h-px bg-border', className)} {...props} />;
}
